import React, { useRef, useState, useEffect } from "react";
import { connect } from "react-redux";
import { makeStyles } from "@mui/styles";
import Autocomplete from "@mui/material/Autocomplete";
import ErrorIcon from "@mui/icons-material/Error";
import IconButton from "@mui/material/IconButton";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import clsx from "clsx";
import { displayDate } from "../reducers/TimeReducer";
import TextFieldPlus from "./TextFieldPlus";
import Tooltip from "../components/Tooltip";
import { sortObjArr } from "../utils/functions";
import { getService } from "../reducers/service";
import { handleError } from "../reducers/ErrorReducer";

const useStyles = makeStyles((theme) => ({
  wrap: {
    position: "relative",
    "& .MuiAutocomplete-clearIndicator": {
      visibility: "visible",
      opacity: 0.2,
      transition: "all 0.1s",
    },
    "&:hover, & .Mui-focused": {
      "& $personInfo": {
        opacity: 1,
      },
      "& $warrants": {
        opacity: 1,
      },
      "& .MuiAutocomplete-clearIndicator": {
        opacity: 1,
      },
    },
  },
  textField: {
    margin: "0 4px 8px",
  },
  activeWarrant: {
    background: theme.palette.error.main,
    color: theme.card.bg,
    width: "calc(100% + 32px)",
    padding: "8px 16px",
    margin: "-8px -16px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: 14,
    "& em": {
      fontStyle: "normal",
      fontSize: 12,
    },
  },
  option: {
    fontSize: 14,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    "& em": {
      fontStyle: "normal",
      fontSize: 12,
    },
  },
  personInfo: {
    position: "absolute",
    right: 60,
    top: 5,
    opacity: 0.2,
    transition: "all 0.1s",
  },
  warrants: {
    position: "absolute",
    right: 90,
    top: 5,
    opacity: 0.2,
    transition: "all 0.1s",
  },
  iconButtonError: {
    color: theme.palette.error.main,
  },
  iconButton: {
    color: theme.palette.secondary.main,
  },
}));

const btnStyle = {
  margin: "0 8px 0 2px",
  padding: 0,
};

const getPersonContactInfo = (ptsPersonID) => {
  const service = getService("cad");
  return service.get({
    type: "get-person-contact-info",
    data: { ptsPersonID },
  });
};

export const getPartyPerson = (ptsPersonID) => {
  const service = getService("cad");
  return service.get({
    type: "get-party-person",
    data: { ptsPersonID },
  });
};

function PersonLookup2(props) {
  const {
    label = "Search Person",
    defaultOptions,
    dictionary,
    dialogs,
    ptsPersonID,
    setPerson,
    setShowAddPersonDialog,
    setPersonDialogSource,
    addEnabled,
  } = props;
  const classes = useStyles();
  const [text, setText] = useState("");
  const [searchMatch, setSearchMatch] = useState([]);
  const [personData, setPersonData] = useState(null);
  const [warrantsNo, setWarrantsNo] = useState(0);
  const [warrants, setWarrants] = useState([]);
  const mountedRef = useRef(true);
  const throttleRef = useRef(0);
  const offset = 0;
  const limit = 100;

  useEffect(() => {
    searchPerson();
    // eslint-disable-next-line
  }, [text]);

  useEffect(() => {
    if (!defaultOptions) return;
    setSearchMatch(defaultOptions);
    // eslint-disable-next-line
  }, [defaultOptions]);

  useEffect(() => {
    if (ptsPersonID !== undefined) getPerson(ptsPersonID);
    // eslint-disable-next-line
  }, [ptsPersonID]);

  useEffect(() => {
    handlePersonSet();
    // eslint-disable-next-line
  }, [setPerson]);

  const getPerson = async (ptsPersonID) => {
    if (!ptsPersonID) return;
    try {
      const personData = await getPartyPerson(ptsPersonID);
      if (!mountedRef.current) return;
      setSearchMatch([personData]);
      setPersonData(personData);
      onSearchSelect(null, personData);
    } catch (err) {
      handleError(err);
    }
  };

  const searchPeople2 = (search = "", ViewDeleted = false, offset, limit) => {
    const service = getService("cad-search-people2");
    return service.find({ query: { search, ViewDeleted, offset, limit } });
  };

  const searchPerson = () => {
    if (text.length < 3) {
      setSearchMatch([]);
      return;
    }
    clearTimeout(throttleRef.current);
    throttleRef.current = setTimeout(() => {
      searchPeople2(text, false, offset, limit)
        .then((result) => {
          result = result.map((p) => ({
            ...p,
            BirthDate: displayDate(p.BirthDate),
          }));
          result = sortResults(result);
          setSearchMatch(result);
        })
        .catch(props.handleError);
    }, 500);
  };

  const sortResults = (results) => {
    let out = [];
    out = sortObjArr(results, "MiddleName");
    out = sortObjArr(out, "FirstName");
    out = sortObjArr(out, "LastName");
    return out;
  };

  const onPersonChange = (data) => {
    if (props.onPersonChange) return props.onPersonChange(data);
    if (props.onChange) return props.onChange(data);
  };

  const onSearchSelect = (ev, selection) => {
    if (!selection) {
      setPersonData(null);
      onPersonChange(null);
      return;
    }
    setWarrantsNo(0);
    setWarrants([]);
    setPersonData(selection);
    getPersonContactInfo(selection.ptsPersonID)
      .then((result) => {
        if (!mountedRef.current) return;
        onPersonChange({ ...selection, ContactInfo: result });
      })
      .catch((err) => props.handleError(err, "Cannot contact API"));
  };

  // If person set by parent component
  const handlePersonSet = (person = setPerson) => {
    if (!person) {
      if (personData) {
        setSearchMatch([]);
        setPersonData(null);
      }
      return;
    } else if (!person.ptsPersonID) return;
    const autocompleteOptions = [{ ...person }];
    setSearchMatch(autocompleteOptions);
    setPersonData(autocompleteOptions[0]);
    if (person !== setPerson) {
      props.onChange(person);
    }
  };

  const handleDialogOpen = () => {
    setShowAddPersonDialog(true);
    setPersonDialogSource("person");
  };

  const renderOption = (props, option) => {
    let label = "";
    if (option.LastName) label += option.LastName + ",";
    if (option.FirstName) label += ` ${option.FirstName} `;
    if (option.MiddleName) label += ` ${option.MiddleName} `;
    label += " | ";
    if (option.Race) {
      const desc = dictionary.dictionary.codeRace.find(
        (s) => s.Code === option.Race
      );
      if (desc) label += " Race: " + desc.Description + " | ";
    }
    if (option.Sex) {
      const desc = dictionary.dictionary.codeSex.find(
        (s) => s.Code === option.Sex
      );
      if (desc) label += " Sex: " + desc.Description + " | ";
    }
    if (option.BirthDate) {
      label += " DOB: " + option.BirthDate + " | ";
    }
    if (option.OLN) label += " OLN: " + option.OLN + " | ";
    if (option.SSN) label += " SSN: " + option.SSN + " | ";
    if (option.PersonID) label += " " + option.PersonID;

    return (
      <li {...props}>
        <span
          className={
            option.ActiveWarrant ? classes.activeWarrant : classes.option
          }
        >
          {label}
        </span>
      </li>
    );
  };

  const renderWarrants = () => {
    const onClick = () => {};
    return (
      <div className={classes.warrants}>
        <Tooltip title="Active warrants">
          <span>
            <IconButton
              color="secondary"
              size="small"
              onClick={onClick}
              className={classes.iconButtonError}
            >
              <ErrorIcon />
            </IconButton>
          </span>
        </Tooltip>
      </div>
    );
  };

  const getByLabelText = (option) => {
    return (
      option.FullName ||
      `${option.FirstName || ""} ${option.MiddleName || ""}, ${
        option.LastName || ""
      }`.trim()
    );
  };

  const startAdornment = addEnabled ? (
    <IconButton style={btnStyle} onClick={() => handleDialogOpen()}>
      <AddCircleOutlineIcon fontSize="small" />
    </IconButton>
  ) : undefined;

  return (
    <div className={clsx(classes.wrap, props.className)}>
      <Autocomplete
        disabled={props.disabled}
        fullWidth
        autoHighlight
        options={searchMatch}
        value={personData}
        getOptionLabel={(option) => {
          return getByLabelText(option);
        }}
        size="small"
        onChange={onSearchSelect}
        onInputChange={(ev, val) => setText(val)}
        isOptionEqualToValue={(option, value) =>
          option.ptsPersonID === value.ptsPersonID
        }
        renderOption={renderOption}
        filterOptions={(x) => x}
        renderInput={(params) => (
          <TextFieldPlus
            {...params}
            label={label}
            variant="outlined"
            inputProps={{
              ...params.inputProps,
              autoComplete: "new-password",
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment,
              endAdornment: (
                <>
                  {params.InputProps.endAdornment}
                  {warrantsNo > 0 && renderWarrants()}
                </>
              ),
            }}
          />
        )}
      />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    dictionary: state.dictionary,
    dataUpdate: state.dataUpdate,
  };
};

export default connect(mapStateToProps, {
  handleError,
})(PersonLookup2);
